import { SaveOutlined, WarningOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { policyActionSchema } from '@salescore/features'
import { Button, Form, Input, Row, Space, Switch, Tooltip, Typography } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { buildUniformActionsWithPermission, type PolicyFormValue } from './policyFormValue'

export const PolicyForm = ({
  onFinish,
  loading,
  initialValue,
  isEditing,
}: {
  onFinish: (value: PolicyFormValue) => Promise<void>
  loading: boolean
  initialValue: PolicyFormValue
  isEditing: boolean
}): ReactElement => {
  const { t } = useTranslation()
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(initialValue.label.trim() === '')
  const { Text } = Typography
  const [form] = useForm()
  const [isAllChecked, setIsAllChecked] = useState<boolean | undefined>()

  return (
    <PageHeader>
      <Form
        form={form}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return -- 保存は非同期なので onFinish は Promise を返すが、antd の onFinish の型は void を期待する
        onFinish={onFinish}
        initialValues={initialValue}
      >
        <Form.Item
          name="label"
          rules={[{ required: true, whitespace: true, message: t(`権限セット名を入力してください`) }]}
        >
          <Input
            size="large"
            variant="borderless"
            placeholder={t(`権限セット名を入力`)}
            className="text-2xl font-bold"
            maxLength={120}
            onChange={(event) => {
              setIsSaveButtonDisabled(event.target.value.trim() === '')
            }}
          />
        </Form.Item>
        <p className="mb-0.5 text-base font-bold">{t(`説明`)} </p>
        <Form.Item name="description">
          <Input.TextArea placeholder={t(`説明を入力`)} showCount maxLength={240} />
        </Form.Item>
        <Row justify="end">
          <Button
            className="mt-2"
            onClick={() => {
              const switchFlag = isAllChecked === undefined ? false : !isAllChecked
              setIsAllChecked(switchFlag)
              form.setFieldsValue({ actionsWithPermission: buildUniformActionsWithPermission(switchFlag) })
            }}
          >
            {isAllChecked === true || isAllChecked === undefined ? t(`全て解除`) : t(`全て許可`)}
          </Button>
        </Row>
        <p className="mb-0.5 text-base font-bold">{t(`シート`)} </p>
        <Form.Item
          label={t(`新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-create']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`作成したシートの設定には「更新」権限も必要です`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`更新`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-update']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-delete']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-create-new-record']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`レコードの追加には「レコードの保存」権限も必要です`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの保存`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-save-record']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-delete-record']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`参照先レコードの新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-open-relation-input-form']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`CSVダウンロード`)}
          name={['actionsWithPermission', policyActionSchema.enum['sheet-download-csv']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`リードの昇格`)}
          name={['actionsWithPermission', policyActionSchema.enum['convert-lead']]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <p className="mb-0.5 text-base font-bold">{t(`KPI`)} </p>
        <Form.Item
          label={t(`新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-create']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`作成したKPIの設定には「更新」権限も必要です`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`更新`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-update']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-delete']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-create-new-record']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`レコードの追加には「レコードの保存」権限も必要です`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの保存`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-save-record']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`レコードの削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-delete-record']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`参照先レコードの新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-open-relation-input-form']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`CSVダウンロード`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-download-csv']]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <p className="mb-0.5 text-base font-bold">{t(`ダッシュボード`)} </p>
        <Form.Item
          label={t(`新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-pivot-create']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`作成したダッシュボードの設定には「更新」権限も必要です`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`更新`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-pivot-update']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-pivot-delete']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`CSVダウンロード`)}
          name={['actionsWithPermission', policyActionSchema.enum['kpi-pivot-download-csv']]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <p className="mb-0.5 text-base font-bold">{t(`フォルダ`)} </p>
        <Form.Item
          label={t(`新規作成`)}
          name={['actionsWithPermission', policyActionSchema.enum['view-group-create']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`削除`)}
          name={['actionsWithPermission', policyActionSchema.enum['view-group-delete']]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <p className="mb-0.5 text-base font-bold">{t(`全般`)} </p>
        <Form.Item
          label={t(`閲覧制限の解除`)}
          name={['actionsWithPermission', policyActionSchema.enum['view-display-config-edit']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={
            <>
              {t(
                `OFFにすると、KPIやシートで列の追加や絞り込み条件等の変更ができなくなります。変更した結果を保存する場合は『更新』をONにしてください`,
              )}
            </>
          }
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`組織設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-organization-setting']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`連携先設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-connection']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={<>{t(`連携先の設定画面の閲覧・操作を制御します`)}</>}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`オブジェクト設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-elt-model']]}
          className="mb-0.5"
          valuePropName="checked"
          tooltip={
            <>{t(`オブジェクト一覧の閲覧・設定を制御します。シートのカスタムカラムの追加はこの権限では制御しません`)}</>
          }
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`目標設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-goal']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`軸グループ設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-dimension-group']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`ユーザー一覧`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-users']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`ユーザーグループ設定`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-user-groups']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`ユーザー招待`)}
          name={['actionsWithPermission', policyActionSchema.enum['manage-invitation']]}
          className="mb-0.5"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label={t(`利用状況閲覧`)}
          name={['actionsWithPermission', policyActionSchema.enum['read-user-activity']]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {isEditing && (
          <Text type="warning">
            <Space>
              <WarningOutlined />
              <span>
                {t(
                  `変更は権限の紐づく全ユーザーに即時反映されます。ユーザーはリロードすると設定した権限が適用されるようになります。`,
                )}
              </span>
            </Space>
          </Text>
        )}
        <Form.Item>
          <Row justify="end">
            <Tooltip title={isSaveButtonDisabled ? t(`権限セット名を入力してください`) : undefined}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
                disabled={isSaveButtonDisabled}
              >
                {t(`保存`)}
              </Button>
            </Tooltip>
          </Row>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}
